import { GailProductRoles } from "@/enums/user";

/** Skip the cross-instance refresh memo so Auth0 is asked for a new AT (role propagation). */
export const FORCE_TOKEN_REFRESH_HEADER = "x-gail-force-refresh";

/**
 * Configuration for Auth0 role propagation polling.
 * Controls the timing and retry behavior of role verification.
 */
export interface Auth0PropagationConfig {
  /** Initial delay before first check (ms) */
  initialDelay: number;
  /** Maximum delay between retries (ms) */
  maxDelay: number;
  /** Maximum number of retry attempts */
  maxRetries: number;
  /** Multiplier for exponential backoff */
  backoffMultiplier: number;
}

export const AUTH0_PROPAGATION_CONFIG: Auth0PropagationConfig = {
  initialDelay: 500,
  maxDelay: 3000,
  maxRetries: 5,
  backoffMultiplier: 1.5,
};

export interface ExpectedProductRoles {
  /** Roles that should be present after the change */
  shouldHave: GailProductRoles[];
  /** Roles that should be absent after the change */
  shouldNotHave: GailProductRoles[];
}

export interface RolePropagationResult {
  /** Whether the role propagation was verified */
  verified: boolean;
}

export interface RefreshSessionResponse {
  user?: {
    roles?: string[];
  };
}

/**
 * Function type for fetching refresh session data.
 * Allows dependency injection for testing.
 */
export type RefreshSessionFetcher = () => Promise<RefreshSessionResponse | null>;

/**
 * Default implementation of refresh session fetcher.
 * Calls the force-refresh endpoint to get a fresh token with updated roles.
 */
export const defaultRefreshSessionFetcher: RefreshSessionFetcher = async () => {
  try {
    const response = await fetch("/api/auth/refresh", {
      method: "POST",
      credentials: "include",
      // Bypass the 120s refresh memo so each poll can ask Auth0 for a token
      // that includes newly assigned org/product roles.
      headers: { [FORCE_TOKEN_REFRESH_HEADER]: "1" },
    });

    if (!response.ok) return null;

    const data = await response.json();
    return { user: { roles: data?.user?.roles } };
  } catch {
    return null;
  }
};

/**
 * Verifies that the current roles match the expected roles.
 *
 * @param currentRoles - The current roles from the session
 * @param expectedRoles - The roles that should/shouldn't be present
 * @returns true if all expected roles are present and all removed roles are absent
 */
export function verifyRoles(currentRoles: string[], expectedRoles: ExpectedProductRoles): boolean {
  // Verify all expected roles are present
  const hasAllExpected = expectedRoles.shouldHave.every((role) => currentRoles.includes(role));

  // Verify all removed roles are absent
  const missingAllRemoved = expectedRoles.shouldNotHave.every(
    (role) => !currentRoles.includes(role)
  );

  return hasAllExpected && missingAllRemoved;
}

/**
 * Calculates the next delay using exponential backoff.
 *
 * @param currentDelay - The current delay in milliseconds
 * @param backoffMultiplier - The multiplier for exponential backoff
 * @param maxDelay - The maximum delay allowed
 * @returns The next delay value, capped at maxDelay
 */
export function calculateNextDelay(
  currentDelay: number,
  backoffMultiplier: number,
  maxDelay: number
): number {
  return Math.min(currentDelay * backoffMultiplier, maxDelay);
}

/**
 * Polls the refresh-session endpoint to verify that role changes have propagated
 * in Auth0 before reloading the page. Uses exponential backoff to balance between
 * responsiveness and avoiding unnecessary API calls.
 *
 * @param expectedProductRoles - Product roles we expect to be present/absent after the change
 * @param config - Optional configuration overrides for testing
 * @param fetcher - Optional custom fetcher for testing
 * @returns Promise that resolves when roles are verified or max retries reached
 */
export async function waitForRolePropagation(
  expectedProductRoles: ExpectedProductRoles,
  config: Auth0PropagationConfig = AUTH0_PROPAGATION_CONFIG,
  fetcher: RefreshSessionFetcher = defaultRefreshSessionFetcher
): Promise<RolePropagationResult> {
  const { initialDelay, maxDelay, maxRetries, backoffMultiplier } = config;

  let delay: number = initialDelay;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    // Wait before checking
    await new Promise((resolve) => setTimeout(resolve, delay));

    try {
      const data = await fetcher();

      if (data) {
        const currentRoles: string[] = data.user?.roles ?? [];

        if (verifyRoles(currentRoles, expectedProductRoles)) {
          return { verified: true };
        }
      }
    } catch (error) {
      console.error(`Role propagation check failed (attempt ${attempt + 1}):`, error);
    }

    // Increase delay with exponential backoff, capped at maxDelay
    delay = calculateNextDelay(delay, backoffMultiplier, maxDelay);
  }

  // Max retries reached without verification
  console.warn(
    "Auth0 role propagation could not be verified after max retries. " +
      "The user may need to refresh manually if roles are not updated."
  );
  return { verified: false };
}
